import type { AxiosInstance } from 'axios';
import type {
  AppDetails,
  AppListResponse,
  ResolveAppDatasourceRequest,
  ResolveAppDatasourceResponse,
  SubmitAppRequest,
  AutomationPartner,
  AutomationPartnerAction,
  CreateAutomationPartnerRequest,
  CreateIntegrationActionRequest,
  CreateWebhookRequest,
  CreateIntegrationFieldRequest,
  CreateIntegrationPartnerRequest,
  DeleteIntegrationActionParams,
  DeleteIntegrationFieldParams,
  GetAutomationPartnerRequest,
  GetAppRequest,
  DeleteIntegrationPartnerParams,
  GetIntegrationHooksCatalogRequest,
  GetWebhooksByCompanyRequest,
  GetIntegrationPartnersByCompanyRequest,
  HookCatalogItem,
  IntegrationAction,
  IntegrationField,
  IntegrationPartner,
  ListAutomationActionsRequest,
  ListAutomationPartnersRequest,
  UpdateIntegrationActionParams,
  UpdateIntegrationActionRequest,
  UpdateIntegrationFieldParams,
  UpdateIntegrationFieldRequest,
} from './contracts';

/**
 * Gateway for interacting with the Integrations API.
 */
export class IntegrationsGateway {
  constructor(
    private readonly httpClient: AxiosInstance,
    private readonly baseUrl: string,
  ) {}

  /**
   * Retrieves the apps available to the authenticated company.
   * @returns The available apps and their total count.
   */
  async getAppList(): Promise<AppListResponse> {
    const { data } = await this.httpClient.get<AppListResponse>(`${this.baseUrl}/apps`);
    return data;
  }

  /**
   * Retrieves an app and its form definition.
   * @param appId - The app identifier.
   * @returns The app details.
   */
  async getApp({ appId }: GetAppRequest): Promise<AppDetails> {
    const { data } = await this.httpClient.get<AppDetails>(`${this.baseUrl}/apps/${appId}`);
    return data;
  }

  /**
   * Resolves the available items for an app datasource using the supplied context.
   * @param appId - The app identifier.
   * @param datasourceId - The datasource identifier.
   * @param context - The JSON context used to resolve the datasource.
   * @returns The resolved datasource items.
   */
  async resolveAppDatasource({
    appId,
    datasourceId,
    context,
  }: ResolveAppDatasourceRequest): Promise<ResolveAppDatasourceResponse> {
    const { data } = await this.httpClient.post<ResolveAppDatasourceResponse>(
      `${this.baseUrl}/apps/${appId}/datasources/${datasourceId}`,
      { context },
    );
    return data;
  }

  /**
   * Submits an app using the supplied context.
   * @param appId - The app identifier.
   * @param context - The JSON context submitted to the app.
   * @returns The response produced by the app submission.
   */
  async submitApp<TResponse = unknown>({ appId, context }: SubmitAppRequest): Promise<TResponse> {
    const { data } = await this.httpClient.post<TResponse>(`${this.baseUrl}/apps/${appId}/submit`, { context });
    return data;
  }

  /**
   * Retrieves all integration partners configured for a company.
   * @param companyId - The company identifier.
   */
  async getPartnersByCompany({
    companyId,
    name,
  }: GetIntegrationPartnersByCompanyRequest): Promise<IntegrationPartner[]> {
    const { data } = await this.httpClient.get<IntegrationPartner[]>(`${this.baseUrl}/partner/company/${companyId}`, {
      params: name ? { name } : undefined,
    });
    return data;
  }

  /** Retrieves the webhooks scenario for a company. */
  async getWebhooksByCompany(params: GetWebhooksByCompanyRequest): Promise<IntegrationPartner[]> {
    const { data } = await this.httpClient.get<IntegrationPartner[]>(`${this.baseUrl}/webhooks`, {
      params,
    });
    return data;
  }

  /**
   * Retrieves the hook catalog, optionally scoped by module.
   * @param module - The optional module identifier used by the Integrations API filter.
   */
  async getHooksCatalog({ module }: GetIntegrationHooksCatalogRequest = {}): Promise<HookCatalogItem[]> {
    const { data } = await this.httpClient.get<HookCatalogItem[]>(`${this.baseUrl}/hooks`, {
      params: module ? { module } : undefined,
    });
    return data;
  }

  /**
   * @param companyId - The optional company identifier.
   */
  async listAutomationPartners({ companyId }: ListAutomationPartnersRequest = {}): Promise<AutomationPartner[]> {
    const { data } = await this.httpClient.get<AutomationPartner[]>(`${this.baseUrl}/automations/partners`, {
      params: companyId ? { companyId } : undefined,
    });
    return data;
  }

  /**
   * Lists actions for an automation partner.
   * @param partnerId - The automation partner identifier.
   */
  async listAutomationActions({ partnerId }: ListAutomationActionsRequest): Promise<AutomationPartnerAction[]> {
    const { data } = await this.httpClient.get<AutomationPartnerAction[]>(
      `${this.baseUrl}/automations/partners/${partnerId}/actions`,
    );
    return data;
  }

  /**
   * Retrieves an automation partner by identifier.
   * @param id - The automation partner identifier.
   */
  async getAutomationPartner({ id }: GetAutomationPartnerRequest): Promise<AutomationPartner> {
    const { data } = await this.httpClient.get<AutomationPartner>(`${this.baseUrl}/automations/partners/${id}`);
    return data;
  }

  /**
   * Creates an automation partner.
   * @param payload - The automation partner creation payload.
   */
  async createAutomationPartner(payload: CreateAutomationPartnerRequest): Promise<AutomationPartner> {
    const { data } = await this.httpClient.post<AutomationPartner>(`${this.baseUrl}/automations/partners`, payload);
    return data;
  }

  /**
   * Creates an integration partner.
   * @param payload - The partner creation payload.
   * @returns The created integration partner.
   */
  async createPartner(payload: CreateIntegrationPartnerRequest): Promise<IntegrationPartner> {
    const { data } = await this.httpClient.post<IntegrationPartner>(`${this.baseUrl}/partner`, payload);
    return data;
  }

  /** Creates a webhook. */
  async createWebhook(payload: CreateWebhookRequest): Promise<IntegrationPartner> {
    const { data } = await this.httpClient.post<IntegrationPartner>(`${this.baseUrl}/webhooks`, payload);
    return data;
  }

  /**
   * Deletes an integration partner.
   * @param partnerId - The partner identifier.
   * @returns A promise that resolves when the partner is deleted.
   */
  async deletePartner({ partnerId }: DeleteIntegrationPartnerParams): Promise<void> {
    await this.httpClient.delete(`${this.baseUrl}/partner/${partnerId}`);
  }

  /**
   * Creates an integration action.
   * @param payload - The action creation payload.
   * @returns The created integration action.
   */
  async createAction(payload: CreateIntegrationActionRequest): Promise<IntegrationAction> {
    const { data } = await this.httpClient.post<IntegrationAction>(`${this.baseUrl}/action`, {
      ...payload,
      actionName: payload.actionName ?? payload.hook,
    });
    return data;
  }

  /**
   * Updates an integration action.
   * @param actionId - The action identifier.
   * @param payload - The action update payload.
   * @returns The updated integration action.
   */
  async updateAction({ actionId, payload }: UpdateIntegrationActionParams): Promise<IntegrationAction> {
    const { data } = await this.httpClient.patch<IntegrationAction>(`${this.baseUrl}/action/${actionId}`, payload);
    return data;
  }

  /**
   * Creates an integration action field/header.
   * @param payload - The field creation payload.
   * @returns The created integration field.
   */
  async createField(payload: CreateIntegrationFieldRequest): Promise<IntegrationField> {
    const { data } = await this.httpClient.post<IntegrationField>(`${this.baseUrl}/field`, payload);
    return data;
  }

  /**
   * Updates an integration action field/header.
   * @param fieldId - The field identifier.
   * @param payload - The field update payload.
   * @returns The updated integration field response.
   */
  async updateField({ fieldId, payload }: UpdateIntegrationFieldParams): Promise<IntegrationField | number[]> {
    const { data } = await this.httpClient.put<IntegrationField | number[]>(
      `${this.baseUrl}/field/${fieldId}`,
      payload,
    );
    return data;
  }

  /**
   * Deletes an integration action field/header.
   * @param fieldId - The field identifier.
   * @returns A promise that resolves when the field is deleted.
   */
  async deleteField({ fieldId }: DeleteIntegrationFieldParams): Promise<void> {
    await this.httpClient.delete(`${this.baseUrl}/field/${fieldId}`);
  }

  /**
   * Deletes an integration action.
   * @param actionId - The action identifier.
   * @returns A promise that resolves when the action is deleted.
   */
  async deleteAction({ actionId }: DeleteIntegrationActionParams): Promise<void> {
    await this.httpClient.delete(`${this.baseUrl}/action/${actionId}`);
  }
}

export type {
  App,
  AppDatasource,
  AppDatasourceItem,
  AppDetails,
  AppField,
  AppForm,
  AppListResponse,
  AppSection,
  ResolveAppDatasourceRequest,
  ResolveAppDatasourceResponse,
  SubmitAppRequest,
  AutomationPartner,
  AutomationPartnerAction,
  CreateAutomationPartnerRequest,
  CreateIntegrationActionRequest,
  CreateWebhookRequest,
  CreateIntegrationFieldRequest,
  CreateIntegrationPartnerRequest,
  DeleteIntegrationActionParams,
  DeleteIntegrationFieldParams,
  GetAutomationPartnerRequest,
  GetAppRequest,
  DeleteIntegrationPartnerParams,
  GetIntegrationHooksCatalogRequest,
  GetWebhooksByCompanyRequest,
  GetIntegrationPartnersByCompanyRequest,
  HookCatalogItem,
  IntegrationAction,
  IntegrationField,
  IntegrationPartner,
  ListAutomationActionsRequest,
  ListAutomationPartnersRequest,
  UpdateIntegrationActionParams,
  UpdateIntegrationActionRequest,
  UpdateIntegrationFieldParams,
  UpdateIntegrationFieldRequest,
} from './contracts';
